import React from 'react'
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Typography,
  Box,
  Chip,
} from '@mui/material'
import {
  ShoppingCart,
  Person,
  Assignment,
  CheckCircle,
  Schedule,
  Error,
} from '@mui/icons-material'
import { formatDistanceToNow } from 'date-fns'

interface Activity {
  id: string
  type: 'order_created' | 'order_updated' | 'employee_created' | 'employee_updated' | 'assignment_created'
  title: string
  description: string
  timestamp: string
  userId?: number
  entityId?: number
}

interface RecentActivityListProps {
  activities: Activity[]
  maxItems?: number
}

const getActivityIcon = (type: string) => {
  switch (type) {
    case 'order_created':
    case 'order_updated':
      return <ShoppingCart />
    case 'employee_created':
    case 'employee_updated':
      return <Person />
    case 'assignment_created':
      return <Assignment />
    default:
      return <CheckCircle />
  }
}

const getActivityColor = (type: string) => {
  switch (type) {
    case 'order_created':
      return 'success.main'
    case 'order_updated':
      return 'warning.main'
    case 'employee_created':
      return 'info.main'
    case 'employee_updated':
      return 'primary.main'
    case 'assignment_created':
      return 'secondary.main'
    default:
      return 'grey.500'
  }
}

const getActivityTypeLabel = (type: string) => {
  switch (type) {
    case 'order_created':
      return 'Order Created'
    case 'order_updated':
      return 'Order Updated'
    case 'employee_created':
      return 'Employee Added'
    case 'employee_updated':
      return 'Employee Updated'
    case 'assignment_created':
      return 'Assignment Created'
    default:
      return 'Activity'
  }
}

export const RecentActivityList: React.FC<RecentActivityListProps> = ({
  activities,
  maxItems = 10,
}) => {
  const displayActivities = activities.slice(0, maxItems)

  if (displayActivities.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: 200,
          color: 'onSurfaceVariant',
          py: 4,
        }}
      >
        <Schedule sx={{ fontSize: 48, mb: 3, opacity: 0.6 }} />
        <Typography variant="body1" color="onSurfaceVariant" sx={{ fontWeight: 400 }}>
          No recent activity
        </Typography>
      </Box>
    )
  }

  return (
    <List sx={{
      width: '100%',
      maxHeight: 400,
      overflow: 'auto',
      py: 1,
      '&::-webkit-scrollbar': {
        width: 6,
      },
      '&::-webkit-scrollbar-track': {
        backgroundColor: 'surfaceContainerHighest',
        borderRadius: 3,
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'outline',
        borderRadius: 3,
        '&:hover': {
          backgroundColor: 'onSurfaceVariant',
        },
      },
    }}>
      {displayActivities.map((activity, index) => (
        <ListItem
          key={activity.id}
          sx={{
            px: 2,
            py: 2,
            borderRadius: 2,
            mb: 1,
            backgroundColor: 'surfaceContainer',
            border: '1px solid',
            borderColor: 'outlineVariant',
            transition: (theme) => theme.transitions.create(
              ['background-color', 'border-color'],
              { duration: theme.transitions.duration.short }
            ),
            '&:hover': {
              backgroundColor: 'surfaceContainerHigh',
              borderColor: 'outline',
            },
            '&:last-child': {
              mb: 0,
            },
          }}
        >
          <ListItemIcon sx={{ minWidth: 48, mr: 2 }}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: getActivityColor(activity.type),
                color: 'white',
              }}
            >
              {React.cloneElement(getActivityIcon(activity.type), {
                sx: { fontSize: 20 }
              })}
            </Avatar>
          </ListItemIcon>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1 }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 500,
                    color: 'onSurface',
                    flex: 1,
                    lineHeight: 1.4,
                  }}
                >
                  {activity.title}
                </Typography>
                <Chip
                  label={getActivityTypeLabel(activity.type)}
                  size="small"
                  variant="filled"
                  sx={{
                    fontSize: '0.75rem',
                    height: 24,
                    backgroundColor: getActivityColor(activity.type),
                    color: 'white',
                    fontWeight: 500,
                    '& .MuiChip-label': {
                      px: 1.5,
                    },
                  }}
                />
              </Box>
            }
            secondary={
              <Box>
                <Typography
                  variant="body2"
                  color="onSurfaceVariant"
                  sx={{
                    mb: 1,
                    lineHeight: 1.43,
                  }}
                >
                  {activity.description}
                </Typography>
                <Typography
                  variant="caption"
                  color="onSurfaceVariant"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: 400,
                  }}
                >
                  {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                </Typography>
              </Box>
            }
          />
        </ListItem>
      ))}
    </List>
  )
}
